import React from 'react'
import { T } from '@transifex/react'

import { createAnalyticsRoutesMap } from './analytics'
import { createRoutesMapAssetManagement } from './asset-management'
import { createAutopilotRoutesMap } from './autopilot'
import { createCompanyIdRoutesMap } from './companyId'
import { createContractorRouteMap } from './contractor'
import { createControlCenterRoutesMap } from './control-center'
import { createCustomerManagementRouteMap } from './customer-management'
import { createDashboardsRoutesMap } from './dashboards'
import { createRoutesMapFleetManagement } from './fleet-management'
import { createHaulerModeRoutesMap } from './hauler-mode'
import { createInsightsRoutesMap } from './insights'
import { createRoutesMapInventory } from './inventory'
import { createInvoicingRoutesMap } from './invoicing'
import { createMarketPlaceRouteMap } from './marketplace'
import { createNotificationsRoutesMap } from './notifications'
import { createOperationManagementRouteMap } from './operation-management'
import { createProjectIdRoutesMap } from './projectId'
import { createRouteStudioRoutesMap } from './route-studio'
import { createSettingsPageRouteMap } from './settings'
import { createTicketsRouteMap } from './tickets'
import { createUsersRouteMap } from './user'
import { pathJoin } from '../../utils'

export const createRouteMapMain = (prefix: string) => ({
  dashboard: pathJoin(prefix)(`dashboard`).chainWithTitle({
    title: <T _str="Dashboard" />,
    breadCrumbTitle: <T _str="Dashboard" />,
  }),
  dashboards: pathJoin(prefix)('dashboards').chainMapAuto(
    createDashboardsRoutesMap,
    {
      title: <T _str="Dashboards" />,
      breadCrumbTitle: <T _str="Dashboards" />,
    }
  ),
  tickets: pathJoin(prefix)('tickets').chainMapAuto(createTicketsRouteMap, {
    title: <T _str="All Tickets" />,
    breadCrumbTitle: <T _str="All Tickets" />,
  }),
  insights: pathJoin(prefix)('insights').chainMapAuto(createInsightsRoutesMap, {
    title: <T _str="Intelligence" />,
    breadCrumbTitle: <T _str="Intelligence" />,
  }),
  'asset-management': pathJoin(prefix)('asset-management').chainMapAuto(
    createRoutesMapAssetManagement
  ),
  'fleet-management': pathJoin(prefix)('fleet-management').chainMapAuto(
    createRoutesMapFleetManagement
  ),
  inventory: pathJoin(prefix)('inventory').chainMapAuto(
    createRoutesMapInventory,
    {
      title: <T _str="Inventory" />,
      breadCrumbTitle: <T _str="Inventory" />,
    }
  ),
  'operation-management': pathJoin(prefix)('operation-management').chainMap(
    createOperationManagementRouteMap(
      pathJoin(prefix)('operation-management').value.slice(1)
    )
  ),
  contractor: pathJoin(prefix)('contractor').chainMap(
    createContractorRouteMap(pathJoin(prefix)('contractor').value.slice(1))
  ),
  invoicing: pathJoin(prefix)('invoicing').chainMapAuto(
    createInvoicingRoutesMap,
    {
      title: <T _str="Invoicing" />,
      breadCrumbTitle: <T _str="Invoicing" />,
    }
  ),
  autopilot: pathJoin(prefix)('autopilot').chainMapAuto(
    createAutopilotRoutesMap,
    {
      title: <T _str="Autopilot" />,
      breadCrumbTitle: <T _str="Autopilot" />,
    }
  ),
  // Hauler Mode (Erhverv) - the commercial-waste module. Registered
  // like route-studio/invoicing: one module path, tabs derived from
  // the registry (src/modules/hauler-mode).
  'hauler-mode': pathJoin(prefix)('hauler-mode').chainMapAuto(
    createHaulerModeRoutesMap,
    {
      title: <T _str="Erhverv" />,
      breadCrumbTitle: <T _str="Erhverv" />,
    }
  ),
  'route-studio': pathJoin(prefix)('route-studio').chainMapAuto(
    createRouteStudioRoutesMap,
    {
      title: <T _str="Route Studio" />,
      breadCrumbTitle: <T _str="Route Studio" />,
    }
  ),
  // Map Planning: the map-based planning workspace (containers + properties
  // on one shared MapKit map). A single page today — planning interactions
  // land as sub-paths here in later steps.
  'map-planning': pathJoin(prefix)('map-planning').chainWithTitle({
    title: <T _str="Map Planning" />,
    breadCrumbTitle: <T _str="Map Planning" />,
  }),
  'control-center': pathJoin(prefix)('control-center').chainMap(
    createControlCenterRoutesMap(
      pathJoin(prefix)('control-center').value.slice(1)
    )
  ),
  reports: pathJoin(prefix)('reports').chain,
  support: pathJoin(prefix)('support').chain,
  settings: pathJoin(prefix)('settings').chainMap(
    createSettingsPageRouteMap(pathJoin(prefix)('settings').value.slice(1))
  ),
  notifications: pathJoin(prefix)('notifications').chainMapAuto(
    createNotificationsRoutesMap,
    {
      title: <T _str="Notifications" />,
      breadCrumbTitle: <T _str="Notifications" />,
    }
  ),
  'send-mqtt': pathJoin(prefix)('send-mqtt').chain,
  analytics: pathJoin(prefix)('analytics').chainMapAuto(
    createAnalyticsRoutesMap,
    {
      title: <T _str="Analytics" />,
      breadCrumbTitle: <T _str="Analytics" />,
    }
  ),
  'customer-management': pathJoin(prefix)('customer-management').chainMap(
    createCustomerManagementRouteMap(
      pathJoin(prefix)('customer-management').value.slice(1)
    )
  ),
  'market-place': pathJoin(prefix)('market-place').chainMapAuto(
    createMarketPlaceRouteMap,
    {
      title: <T _str="Marketplace" />,
      breadCrumbTitle: <T _str="Marketplace" />,
    }
  ),
  profile: pathJoin(prefix)('profile').chainMapAuto(createUsersRouteMap, {
    title: <T _str="User Profile" />,
    breadCrumbTitle: <T _str="User Profile" />,
  }),
  'users/:userId': (userId: string) =>
    pathJoin(prefix)('users')(userId).chainMapAuto(createUsersRouteMap, {
      title: <T _str="User Details" />,
      breadCrumbTitle: <T _str="User Details" />,
    }),
  'project/:id': (id = ':id') =>
    pathJoin(prefix)(`project/${id}`).chainMapAuto(createProjectIdRoutesMap),
  'company/:id': (id = ':id') =>
    pathJoin(prefix)(`company/${id}`).chainMapAuto(createCompanyIdRoutesMap),
})
